// Funding nakijken, deel vinden (fase 3, owner-besluit G1 a, 06-10-2026).
//
// De vindlaan neemt een regeling die de poort haalt AUTOMATISCH op in het
// register, met het label "nieuw, nog niet nagekeken". Hier kijkt een mens:
//  · "Nieuw, nog niet nagekeken": Opnemen (het label gaat weg) of Afwijzen (met
//    een reden; de regeling verdwijnt van de Funding-tab, niets wordt gewist);
//  · "Vervallen?": een registerregeling waarvan elke pagina dood is, die niet
//    meer op de overzichtspagina staat en geen bekendmaking kreeg — "Klopt,
//    vervallen" (van de tab) of "Bestaat nog".
//
// Zelfde houding als de controles erboven: de SERVER sorteert, telt en
// controleert (een afwijzing zonder reden is een 400 met een zin); hier wordt
// alleen getoond. Optimistisch: een klik toont het besluit meteen; faalt de
// POST, dan gaat de rij terug en staat de zin van de server eronder.

import { useId, useState, type CSSProperties } from "react";

import { Card } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type {
  FundingVondst,
  FundingVondstenResponse,
  FundingVondstGenomenBesluit,
} from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import { datum, noot } from "./BronnenStukken";
import { invoer, knopDonker, knopLicht } from "./stijl";
import { leesbaar } from "./woorden";

type Groep = "open" | "afgehandeld";
type Sectie = "nieuw" | "vervallen";

const KOP: Record<Sectie, string> = {
  nieuw: "Nieuw, nog niet nagekeken",
  vervallen: "Vervallen?",
};
const UITLEG: Record<Sectie, string> = {
  nieuw:
    "Regelingen die de vindlaan vond en die de poort haalden. Ze staan al in het register, met het " +
    "label 'nieuw, nog niet nagekeken'. Opnemen haalt het label weg; afwijzen haalt de regeling van " +
    "de Funding-tab (met jouw reden; niets wordt gewist).",
  vervallen:
    "Registerregelingen waarvan elke pagina onbereikbaar was, die niet meer op de overzichtspagina " +
    "van de verstrekker staan en geen bekendmaking kregen. Een vraag, geen besluit: klopt het, dan " +
    "verdwijnt de regeling van de tab.",
};

export function FundingVondsten() {
  const [groep, setGroep] = useState<Groep>("open");
  // Via een belofte: een fout bij het opvragen (ook een ontbrekende methode)
  // wordt de foutzin van deze sectie, en raakt de controles erboven niet.
  const lijst = useResource<FundingVondstenResponse>(
    () => Promise.resolve().then(() => api.fundingVondsten({ groep })),
    [groep],
  );
  const [eigen, setEigen] = useState<Record<string, FundingVondst>>({});
  const [fouten, setFouten] = useState<Record<string, string>>({});
  const [bezig, setBezig] = useState<Record<string, boolean>>({});
  const [melding, setMelding] = useState("");
  const d = lijst.data;

  async function besluit(item: FundingVondst, keuze: FundingVondstGenomenBesluit, reden?: string) {
    const label = [...(d?.besluiten.nieuw ?? []), ...(d?.besluiten.vervallen ?? [])].find(
      (b) => b.sleutel === keuze,
    )?.label ?? keuze;
    setFouten(({ [item.id]: _weg, ...rest }) => rest);
    setEigen((e) => ({
      ...e,
      [item.id]: { ...item, besluit: keuze, besluitLabel: label, besluitReden: reden?.trim() || null },
    }));
    setBezig((b) => ({ ...b, [item.id]: true }));
    setMelding(`Wordt opgeslagen: ${label} — ${item.titel}.`);
    try {
      const terug = await api.fundingVondstBesluit(item.id, { besluit: keuze, reden: reden ?? null });
      setEigen((e) => ({ ...e, [item.id]: terug }));
      setMelding(`Opgeslagen: ${label} — ${item.titel}.`);
      return true;
    } catch (err) {
      setEigen(({ [item.id]: _weg, ...rest }) => rest);
      setFouten((f) => ({
        ...f,
        [item.id]: leesbaar(err, "Het besluit kon niet worden opgeslagen — probeer het zo nog eens."),
      }));
      setMelding("");
      return false;
    } finally {
      setBezig(({ [item.id]: _klaar, ...rest }) => rest);
    }
  }

  return (
    <section data-vondsten style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div role="group" aria-label="Welke vondsten" style={groepKnoppen}>
        {(["open", "afgehandeld"] as const).map((g) => (
          <button
            key={g}
            type="button"
            className={"view-btn" + (groep === g ? " active" : "")}
            aria-pressed={groep === g}
            onClick={() => setGroep(g)}
          >
            {g === "open" ? "Open vondsten" : "Afgehandelde vondsten"}
          </button>
        ))}
      </div>
      {lijst.error ? (
        <Card style={{ padding: "14px 18px" }}>
          <p data-vondsten-fout style={{ fontSize: size.meta, color: color.accentRed, margin: 0 }}>
            {`De vondsten konden niet worden opgehaald: ${lijst.error}`}
          </p>
        </Card>
      ) : !d ? (
        <EmptyReason reason="de vondsten worden opgehaald" />
      ) : (
        (["nieuw", "vervallen"] as const).map((s) => (
          <SectieKaart
            key={s}
            sectie={s}
            items={d[s].map((i) => eigen[i.id] ?? i)}
            tellingOpen={d.tellingen[s].open}
            besluiten={d.besluiten[s]}
            leeg={groep === "open" ? d.leeg[s] : "Niets in deze selectie."}
            fouten={fouten}
            bezig={bezig}
            stale={lijst.stale}
            onBesluit={besluit}
          />
        ))
      )}
      <div aria-live="polite" data-vondst-status>
        {melding && <p style={{ ...noot, color: color.body }}>{melding}</p>}
      </div>
    </section>
  );
}

const groepKnoppen: CSSProperties = {
  display: "inline-flex",
  alignSelf: "flex-start",
  gap: 4,
  background: color.lineFaint,
  borderRadius: radius.input,
  padding: 3,
};

function SectieKaart({
  sectie,
  items,
  tellingOpen,
  besluiten,
  leeg,
  fouten,
  bezig,
  stale,
  onBesluit,
}: {
  sectie: Sectie;
  items: FundingVondst[];
  tellingOpen: number;
  besluiten: { sleutel: FundingVondstGenomenBesluit; label: string }[];
  leeg: string;
  fouten: Record<string, string>;
  bezig: Record<string, boolean>;
  stale: boolean;
  onBesluit: (item: FundingVondst, keuze: FundingVondstGenomenBesluit, reden?: string) => Promise<boolean>;
}) {
  const kopId = useId();
  return (
    <Card style={{ padding: "16px 18px" }}>
      <section aria-labelledby={kopId} data-sectie={sectie}>
        <h3 id={kopId} style={{ ...sectionLabel, marginBottom: 6 }}>
          {`${KOP[sectie]} · ${tellingOpen} open`}
        </h3>
        <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55, margin: 0 }}>{UITLEG[sectie]}</p>
        {items.length === 0 ? (
          <p data-leeg style={{ fontSize: size.body, color: color.body, margin: "10px 0 0" }}>
            {leeg}
          </p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0", opacity: stale ? 0.6 : 1 }}>
            {items.map((item, i) => (
              <VondstRij
                key={item.id}
                item={item}
                eerste={i === 0}
                besluiten={besluiten}
                fout={fouten[item.id] ?? null}
                bezig={Boolean(bezig[item.id])}
                onBesluit={(keuze, reden) => onBesluit(item, keuze, reden)}
              />
            ))}
          </ul>
        )}
      </section>
    </Card>
  );
}

function VondstRij({
  item,
  eerste,
  besluiten,
  fout,
  bezig,
  onBesluit,
}: {
  item: FundingVondst;
  eerste: boolean;
  besluiten: { sleutel: FundingVondstGenomenBesluit; label: string }[];
  fout: string | null;
  bezig: boolean;
  onBesluit: (keuze: FundingVondstGenomenBesluit, reden?: string) => Promise<boolean>;
}) {
  const [afwijzen, setAfwijzen] = useState(false);
  const [reden, setReden] = useState("");
  const [proef, setProef] = useState<string | null>(null);
  const redenId = useId();
  const foutId = useId();
  const wie = item.regeling ?? item.titel;
  const zekerheid = item.zekerheid === null ? null : Math.round(item.zekerheid * 100);

  async function opslaan() {
    if (!reden.trim()) {
      setProef("Zeg in een paar woorden waarom je afwijst.");
      return;
    }
    setProef(null);
    if (await onBesluit("afwijzen", reden)) setAfwijzen(false);
  }

  return (
    <li
      data-vondst={item.id}
      style={{
        borderTop: eerste ? "none" : `1px solid ${color.lineFaint}`,
        padding: "10px 0",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline" }}>
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: size.body, fontWeight: weight.bold, color: color.ink, overflowWrap: "anywhere" }}
        >
          {item.titel || <EmptyReason reason="titel onbekend" />}
        </a>
        <Pil tekst={item.bronLabel} />
        <Pil tekst={item.statusLabel} />
        {item.besluit !== "open" && <Pil tekst={item.besluitLabel} sterk />}
      </div>
      <div style={{ fontSize: 12, color: color.soft, lineHeight: 1.5 }}>
        {item.reden || <EmptyReason reason="geen reden vastgelegd" />}
        {item.model && ` · oordeel: ${item.model}`}
        {zekerheid !== null && ` · zekerheid ${zekerheid}%`}
        {item.verwantAan && ` · noemt regeling ${item.verwantAan}`}
        {item.gevondenOp && ` · gevonden ${datum(item.gevondenOp) ?? item.gevondenOp}`}
      </div>
      {item.besluit !== "open" && (item.besluitDoor || item.besluitReden) && (
        <div style={{ fontSize: 12, color: color.body }}>
          {[item.besluitDoor && `door ${item.besluitDoor}`, item.besluitOp && (datum(item.besluitOp) ?? item.besluitOp),
            item.besluitReden && `reden: ${item.besluitReden}`]
            .filter(Boolean)
            .join(" · ")}
        </div>
      )}
      <div role="group" aria-label={`Besluit over ${wie}`} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {besluiten.map((b) => (
          <button
            key={b.sleutel}
            type="button"
            disabled={bezig}
            aria-pressed={item.besluit === b.sleutel}
            aria-expanded={b.sleutel === "afwijzen" ? afwijzen : undefined}
            onClick={() => {
              if (b.sleutel === "afwijzen") {
                setProef(null);
                setAfwijzen((x) => !x);
              } else {
                void onBesluit(b.sleutel);
              }
            }}
            style={knop(item.besluit === b.sleutel)}
          >
            {b.label}
          </button>
        ))}
      </div>
      {afwijzen && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void opslaan();
          }}
          style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}
        >
          <label
            htmlFor={redenId}
            style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: color.soft, flex: "1 1 240px" }}
          >
            Waarom wijs je af?
            <input
              id={redenId}
              type="text"
              maxLength={500}
              value={reden}
              placeholder="bijvoorbeeld: dit is een nieuwsbericht, geen regeling"
              onChange={(e) => setReden(e.target.value)}
              aria-invalid={proef ? true : undefined}
              aria-describedby={proef ? foutId : undefined}
              style={{ ...invoer, padding: "7px 10px" }}
            />
          </label>
          <button type="submit" disabled={bezig} style={knopDonker(bezig)}>
            Afwijzen
          </button>
          <button type="button" onClick={() => setAfwijzen(false)} style={knopLicht}>
            Annuleer
          </button>
          {proef && (
            <p id={foutId} role="alert" style={{ flexBasis: "100%", fontSize: size.meta, color: color.accentRed, margin: 0 }}>
              {proef}
            </p>
          )}
        </form>
      )}
      {fout && (
        <p role="alert" data-fout style={{ fontSize: size.meta, color: color.accentRed, margin: 0 }}>
          {fout}
        </p>
      )}
    </li>
  );
}

function Pil({ tekst, sterk }: { tekst: string; sterk?: boolean }) {
  return (
    <span
      style={{
        background: sterk ? color.ink : color.lineFaint,
        color: sterk ? color.surface : color.body,
        border: `1px solid ${color.line}`,
        borderRadius: radius.pill,
        padding: "2px 8px",
        fontSize: size.label,
        fontWeight: weight.medium,
        whiteSpace: "nowrap",
      }}
    >
      {tekst}
    </span>
  );
}

function knop(actief: boolean): CSSProperties {
  return actief
    ? { ...knopLicht, background: color.ink, color: color.surface, borderColor: color.ink }
    : knopLicht;
}
